import { HttpMethod } from '@libs/core';

import type { SignedUrlResponse } from '@/types/api';

import type {
    BulkPromoFormInterface,
    BulkPromoGenerationInterface,
    BulkPromoStatus,
    CartDiscountInterface,
} from '../types';

export const fetchCartDiscounts = async (country: 'US' | 'CA'): Promise<CartDiscountInterface[]> => {
    try {
        const response = await fetch(`/api/bulkPromoJob/cartDiscounts?country=${country}`);

        if (!response.ok) {
            throw new Error('Failed to fetch cart discounts');
        }

        const { data } = await response.json();
        return data;
    } catch (err) {
        console.error('Error fetching cart discounts:', err);
        return [];
    }
};

export const prepareBulkPromoPayload = (
    formData: BulkPromoFormInterface,
    cartDiscountsData: CartDiscountInterface[]
) => {
    const selectedDiscount = cartDiscountsData.find(discount => discount.key === formData.cartDiscount);

    return {
        quantity: Number(formData.quantity),
        prefix: formData.prefix || '',
        name: {
            'en-US': formData.discountCodeName,
        },
        description: {
            'en-US': formData.discountCodeDescription,
        },
        validFrom: formData.validFrom?.toISOString() ?? '',
        validTo: formData.validTo?.toISOString() ?? '',
        maxApplications: formData.maxApplications ? Number(formData.maxApplications) : 1,
        maxApplicationsPerCustomer: formData.maxApplicationsPerCustomer
            ? Number(formData.maxApplicationsPerCustomer)
            : 1,
        cartDiscount: selectedDiscount ? [{ id: selectedDiscount.id, key: selectedDiscount.key }] : [],
        groups: formData.groups,
    };
};

export const submitBulkPromoGeneration = async (
    values: BulkPromoFormInterface,
    cartDiscountsData: CartDiscountInterface[]
): Promise<BulkPromoGenerationInterface | null> => {
    try {
        const formData = prepareBulkPromoPayload(values, cartDiscountsData);

        const response = await fetch('/api/bulkPromoJob', {
            method: HttpMethod.POST,
            body: JSON.stringify(formData),
        });

        if (!response.ok) {
            throw new Error(`Failed to submit Bulk Promo Offer`);
        }

        const { data } = await response.json();
        return data;
    } catch (err) {
        console.error('Failed to submit Bulk Promo Offer', err);
        return null;
    }
};

export const exportBulkPromoJob = async (jobId: string): Promise<boolean> => {
    try {
        const res = await fetch(`/api/bulkPromoJob/${jobId}/export`, {
            method: HttpMethod.POST,
        });
        if (!res.ok) {
            console.error('Failed to export bulk promo job', res.status);
            return false;
        }
        return true;
    } catch (err) {
        console.error('Error exporting bulk promo job', err);
        return false;
    }
};

export const fetchBulkPromoByCriteria = async (status?: BulkPromoStatus) => {
    const url = status ? `/api/bulkPromoJob?status=${status}` : '/api/bulkPromoJob';
    try {
        const response = await fetch(url);

        if (!response.ok) {
            throw new Error('Failed to fetch bulk promo by criteria');
        }

        const { data } = await response.json();
        return data;
    } catch (err) {
        console.error('Error fetching bulk promo by criteria:', err);
        return [];
    }
};

export const downloadCSVBulkPromoJob = async (jobId: string): Promise<void> => {
    try {
        const res = await fetch(`/api/bulkPromoJob/${jobId}/download`);

        const text = await res.text();
        let json: SignedUrlResponse | null = null;
        try {
            json = JSON.parse(text) as SignedUrlResponse;
        } catch {
            json = null;
        }

        if (!res.ok) {
            const message = json?.error ?? `Failed to fetch signed URL: ${res.status} ${res.statusText}`;
            throw new Error(message);
        }

        const url = json?.data?.url;
        if (typeof url !== 'string' || !url) {
            throw new Error('Signed URL not returned by API');
        }

        window.location.href = url;
    } catch (err) {
        console.error('Error downloading bulk promo job', err);
        throw err;
    }
};
